import type { LotView } from "@hammer/core";
import { formatMoney } from "@hammer/core/money";
import { Link } from "@tanstack/react-router";
import { memo } from "react";
import { useArchiveWithUndo, useDeleteWithUndo, useStanceWithUndo } from "../lib/queries.ts";
import { LotFlags, Price, sourceLabel, StanceControl, Thumb, TimeLeft } from "./lot-bits.tsx";

export const LotRow = memo(function LotRow({ lot, selected, onFocus }: { lot: LotView; selected?: boolean; onFocus?: () => void }) {
  const setStance = useStanceWithUndo();
  const archive = useArchiveWithUndo();
  const remove = useDeleteWithUndo();
  const archived = !!lot.archivedAt;
  const meta = [sourceLabel(lot), lot.locationText, lot.lotNumber ? `lot ${lot.lotNumber}` : null].filter(Boolean) as string[];
  return (
    <div className="lot-row" role="row" aria-selected={selected} data-stance={lot.stance.stance} data-lot={lot.id} onClick={onFocus}>
      <Link to="/lot/$id" params={{ id: lot.id }} className="r-img" tabIndex={-1} aria-hidden>
        <Thumb src={lot.images[0]} />
      </Link>
      <div className="r-main">
        {/* The title goes to the listing itself: that's where you bid, and where the bookmarklet updates the lot. */}
        <a href={lot.url} target="_blank" rel="noopener noreferrer" className="r-title" title="Open the listing">{lot.title}</a>
        <div className="r-meta">{meta.map((m) => <span key={m}>{m}</span>)}</div>
        <LotFlags lot={lot} />
      </div>
      <div className="r-price"><Price lot={lot} /></div>
      <div className="r-time"><TimeLeft lot={lot} /></div>
      <div className="r-act" onClick={(e) => e.stopPropagation()}>
        <StanceControl value={lot.stance.stance} onChange={(s) => setStance(lot, s)} />
        <span className="r-sub text-[12.5px] muted num">
          {lot.stance.stance === "bidding" && lot.stance.maxBidCents != null && <span>max {formatMoney(lot.stance.maxBidCents)}</span>}
          <Link to="/lot/$id" params={{ id: lot.id }} className="underline underline-offset-2 hover:text-ink">Details</Link>
          <span className="r-icons">
            {archived ? (
              // In the Archived list, Restore is the main thing to do, so it gets words, not just an icon.
              <button type="button" className="icon-btn icon-btn-wide" onClick={() => archive(lot, false)} aria-label={`Restore ${lot.title}`} title="Put it back on the agenda">
                <RestoreIcon /> Restore
              </button>
            ) : (
              <button type="button" className="icon-btn" onClick={() => archive(lot, true)} aria-label={`Archive ${lot.title}`} title="Archive (e)">
                <ArchiveIcon />
              </button>
            )}
            <button type="button" className="icon-btn icon-btn-danger" onClick={() => remove(lot)} aria-label={`Delete ${lot.title}`} title="Delete">
              <XIcon />
            </button>
          </span>
        </span>
      </div>
    </div>
  );
});

const ArchiveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="1.75" y="2.5" width="12.5" height="3.5" rx="1" />
    <path d="M3 6v6.5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6M6.5 9h3" />
  </svg>
);
const RestoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 8a5 5 0 1 0 1.5-3.55M3 2.5V5h2.5" />
  </svg>
);
const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <path d="M4 4l8 8M12 4l-8 8" />
  </svg>
);
